import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  Gift,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  captureReferralFromUrl,
  fetchReferrerCard,
  logReferralClick,
  type ReferrerCard,
} from "@/lib/referral";
import WeKitLogo from "@/components/WeKitLogo";
import { Button } from "@/components/ui/button";

/**
 * /ref/:handle — branded landing for friends invited by a Wekiteer.
 * Captures the referral code in localStorage and shows the inviter's
 * first name + avatar with a clear CTA into signup.
 */
export default function RefRedirect() {
  const { handle } = useParams<{ handle: string }>();
  const [card, setCard] = useState<ReferrerCard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!handle) return;
    captureReferralFromUrl(handle);
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source") || params.get("src") || "direct";
    logReferralClick(handle, source);
    let active = true;
    fetchReferrerCard(handle).then((c) => {
      if (!active) return;
      setCard(c);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [handle]);

  if (!handle) return <Navigate to="/" replace />;

  const inviter = card?.firstName ?? "A Wekiteer";
  const initials = inviter.slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, hsl(var(--primary) / 0.18), transparent 70%)",
        }}
      />

      <header className="relative z-10 px-4 py-4 max-w-5xl mx-auto">
        <Link to="/" className="inline-flex">
          <WeKitLogo size="sm" />
        </Link>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-5 pt-8 pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Inviter chip */}
          <div className="inline-flex items-center gap-3 rounded-full border border-primary/30 bg-primary/10 pl-1.5 pr-4 py-1.5 text-sm">
            {loading ? (
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            ) : card?.avatarUrl ? (
              <img
                src={card.avatarUrl}
                alt=""
                className="h-8 w-8 rounded-full object-cover border border-primary/30"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-display font-bold text-sm">
                {initials}
              </div>
            )}
            <span className="text-foreground">
              <span className="font-semibold">{inviter}</span>{" "}
              <span className="text-muted-foreground">invited you</span>
            </span>
          </div>

          <h1 className="mt-8 font-display text-4xl sm:text-5xl md:text-6xl font-bold text-foreground leading-[1.05] tracking-tight">
            Your friend just gave you{" "}
            <span className="gradient-text">₹100 off</span>
          </h1>

          <p className="mt-5 text-muted-foreground text-base sm:text-lg max-w-lg mx-auto">
            WeKIT™ Career Clarity 360 maps your strengths to 5,000+ careers of the future in
            30–60 minutes. Your discount is saved and applies at checkout —
            automatically.
          </p>

          {/* Gift card */}
          <div className="mt-8 mx-auto max-w-md rounded-3xl border border-border bg-card/60 backdrop-blur p-6 text-left shadow-lg">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                <Gift className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-display font-semibold text-foreground">
                  ₹100 referral credit
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Auto-applied at checkout · No code needed
                </p>
              </div>
              <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="min-h-[52px] px-8 gap-2 w-full sm:w-auto">
              <Link to="/auth">
                Start my assessment <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="min-h-[52px] px-6 w-full sm:w-auto"
            >
              <Link to="/">Learn more first</Link>
            </Button>
          </div>

          <p className="mt-8 text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            No Child Left Behind in the Age of AI
          </p>
        </motion.div>
      </main>

      {loading && (
        <div className="sr-only">
          <Loader2 className="animate-spin" /> Loading inviter
        </div>
      )}
    </div>
  );
}
